import type { PageInfo, Product } from 'apps/commerce/types.ts'
import { getCheapestVariantOffers } from 'site/utils/getCheapestVariantOffers.ts'

export interface AlgoliaHitInsight {
    objectId: string
    queryId: string
    position: number
}

export interface AlgoliaInsights {
    indexName: string
    queryId: string
    hits: AlgoliaHitInsight[]
}

const DEFAULT_INDEX = 'products'

function parseProductUrl(url: string | undefined, baseUrl?: string) {
    if (!url) return null

    try {
        return new URL(url, baseUrl ?? 'https://localhost')
    } catch {
        return null
    }
}

export function getAlgoliaQueryIdFromProduct(
    product: Product | undefined,
    baseUrl?: string,
): string | undefined {
    const parsed = parseProductUrl(product?.url, baseUrl)
    return parsed?.searchParams.get('algoliaQueryID') ?? undefined
}

export function getAlgoliaIndexFromProduct(
    product: Product | undefined,
    baseUrl?: string,
): string | undefined {
    const parsed = parseProductUrl(product?.url, baseUrl)
    return parsed?.searchParams.get('algoliaIndex') ?? undefined
}

export function appendAlgoliaParamsToUrl(
    url: string | undefined,
    metadata: { queryId: string; indexName: string; objectId: string },
    baseUrl?: string,
): string | undefined {
    if (!url) return undefined

    const parsed = parseProductUrl(url, baseUrl)
    if (!parsed) return url

    parsed.searchParams.set('algoliaQueryID', metadata.queryId)
    parsed.searchParams.set('algoliaIndex', metadata.indexName)
    parsed.searchParams.set('algoliaObjectID', metadata.objectId)

    return `${parsed.pathname}${parsed.search}`
}

export function getAlgoliaObjectIdFromProduct(
    product: Product | undefined,
    baseUrl?: string,
): string | undefined {
    const parsed = parseProductUrl(product?.url, baseUrl)
    return parsed?.searchParams.get('algoliaObjectID') ?? product?.productID
}

export function mergeAlgoliaProductsWithVtex(
    algoliaProducts: Product[],
    vtexProducts: Product[],
    baseUrl?: string,
): Product[] {
    const metadataByGroupId = new Map<
        string,
        { objectId: string; queryId: string; indexName: string }
    >()

    for (const product of algoliaProducts) {
        const groupId = product.inProductGroupWithID
        if (!groupId) continue

        const queryId = getAlgoliaQueryIdFromProduct(product, baseUrl)
        if (!queryId) continue

        metadataByGroupId.set(String(groupId), {
            objectId: product.productID,
            queryId,
            indexName: getAlgoliaIndexFromProduct(product, baseUrl) ?? DEFAULT_INDEX,
        })
    }

    const vtexByGroupId = new Map(
        vtexProducts.filter(Boolean).map((product) => [String(product.inProductGroupWithID), product]),
    )

    return algoliaProducts
        .map((algoliaProduct) => {
            const groupId = String(algoliaProduct.inProductGroupWithID)
            const vtexProduct = vtexByGroupId.get(groupId)
            const metadata = metadataByGroupId.get(groupId)

            if (!vtexProduct || !metadata) return null

            return {
                ...vtexProduct,
                url: appendAlgoliaParamsToUrl(vtexProduct.url, metadata, baseUrl),
            } as Product
        })
        .filter((product): product is Product => product !== null)
}

export function buildAlgoliaInsights(
    algoliaProducts: Product[],
    displayedProducts: Product[],
    pageInfo: PageInfo,
    startingPage = 1,
    baseUrl?: string,
): AlgoliaInsights | null {
    const objectIdByGroupId = new Map(
        algoliaProducts.map((product) => [String(product.inProductGroupWithID), product.productID]),
    )

    const hits: AlgoliaHitInsight[] = []
    let indexName = DEFAULT_INDEX
    let queryId = ''

    displayedProducts.forEach((product, index) => {
        const parsedQueryId = getAlgoliaQueryIdFromProduct(product, baseUrl)
        const objectId = objectIdByGroupId.get(String(product.inProductGroupWithID))

        if (!parsedQueryId || !objectId) return

        indexName = getAlgoliaIndexFromProduct(product, baseUrl) ?? indexName
        queryId = parsedQueryId

        hits.push({
            objectId: getAlgoliaObjectIdFromProduct(product, baseUrl) ?? objectId,
            queryId: parsedQueryId,
            position: index + 1 +
                (pageInfo.currentPage - startingPage) * (pageInfo.recordPerPage ?? 0),
        })
    })

    if (!queryId || hits.length === 0) return null

    return { indexName, queryId, hits }
}

export function buildAlgoliaInsightsFromProducts(
    products: Product[],
    baseUrl?: string,
): AlgoliaInsights | null {
    const hits: AlgoliaHitInsight[] = []
    let indexName = DEFAULT_INDEX
    let queryId = ''

    products.forEach((product, index) => {
        const parsedQueryId = getAlgoliaQueryIdFromProduct(product, baseUrl)
        if (!parsedQueryId) return

        indexName = getAlgoliaIndexFromProduct(product, baseUrl) ?? indexName
        queryId = parsedQueryId

        hits.push({
            objectId: getAlgoliaObjectIdFromProduct(product, baseUrl) ?? product.productID,
            queryId: parsedQueryId,
            position: index + 1,
        })
    })

    if (!queryId || hits.length === 0) return null

    return { indexName, queryId, hits }
}

export function getAlgoliaHitInsight(
    insights: AlgoliaInsights | null | undefined,
    index: number,
): AlgoliaHitInsight | undefined {
    return insights?.hits[index]
}

export function isProductInStock(product: Product): boolean {
    if (product.isVariantOf?.hasVariant?.length) {
        return product.isVariantOf.hasVariant.some((variant) =>
            variant.offers?.offers?.some((offer) =>
                offer.availability === 'https://schema.org/InStock'
            )
        )
    }

    return product.offers?.offers?.[0]?.availability === 'https://schema.org/InStock'
}

export function removeSkuIdFromUrl(url: string | undefined, baseUrl?: string): string | undefined {
    if (!url) return undefined

    const parsed = parseProductUrl(url, baseUrl)
    if (!parsed) return url

    parsed.searchParams.delete('skuId')

    const search = parsed.searchParams.toString()
    return search ? `${parsed.pathname}?${search}` : parsed.pathname
}

export function sanitizeSearchProduct(product: Product, baseUrl?: string): Product {
    const hasVariants = product.isVariantOf?.hasVariant?.length
    const cheapestOffers = hasVariants
        ? getCheapestVariantOffers(product.isVariantOf!.hasVariant)
        : undefined

    return {
        ...product,
        url: removeSkuIdFromUrl(product.url, baseUrl) ?? product.url,
        offers: cheapestOffers ?? product.offers,
    }
}

export function sanitizeSearchProducts(
    products: (Product | null | undefined)[],
    baseUrl?: string,
): Product[] {
    return products
        .filter((product): product is Product => !!product?.name && isProductInStock(product))
        .map((product) => sanitizeSearchProduct(product, baseUrl))
}
